/**
 * Engage Real-Time Monitor
 *
 * Live view of active sessions with condition detection and guidance.
 */

import { useState, useEffect, useRef } from 'react'
import { useQuery } from '@tanstack/react-query'
import { motion, AnimatePresence } from 'framer-motion'
import { api } from '@personic/shared'
import ConditionBadge from '@/components/engage/ConditionBadge'
import StreamModeIndicator from '@/components/engage/StreamModeIndicator'
import TrajectoryForecastMini from '@/components/engage/TrajectoryForecastMini'
import DualStreamPanel from '@/components/engage/DualStreamPanel'
import SignalFeedbackModal from '@/components/engage/SignalFeedbackModal'
import { useTrajectoryForecast } from '@/hooks/usePragmatic'
import type { IdentityMode } from '@personic/shared'

interface Session {
  id: string
  status: string
  channel: string
  agent_id?: string
  profile_id?: string
  started_at: string
  message_count: number
  condition_count: number
  guidance_count: number
  last_activity_at: string
}

interface SessionDetail {
  id: string
  messages: Array<{
    id: string
    speaker: string
    content: string
    timestamp: string
  }>
  conditions: Array<{
    id: string
    condition_type: string
    confidence: number
    intensity?: number
    detected_at: string
  }>
  guidance: Array<{
    id: string
    guidance_type: string
    content: Record<string, unknown>
    priority: string
    generated_at: string
  }>
}

export default function EngageMonitor() {
  const [selectedSession, setSelectedSession] = useState<string | null>(null)
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'ended'>('active')
  const [showDualStream, setShowDualStream] = useState(false)
  const [feedbackModal, setFeedbackModal] = useState<{
    open: boolean
    signalType?: 'certainty' | 'hedging' | 'commitment'
    interpretation?: string
  }>({ open: false })
  const [streamMode] = useState<IdentityMode>('both')
  const messagesEndRef = useRef<HTMLDivElement>(null)

  // Fetch sessions list
  const { data: sessions, isLoading: sessionsLoading } = useQuery<Session[]>({
    queryKey: ['engage-sessions', statusFilter],
    queryFn: async () => {
      const params = statusFilter !== 'all' ? `?status=${statusFilter}` : ''
      const response = await api.get(`/api/engage/v1/sessions${params}`)
      return response.data.sessions
    },
    refetchInterval: 5000,
  })

  // Fetch selected session detail
  const { data: sessionDetail } = useQuery<SessionDetail>({
    queryKey: ['engage-session-detail', selectedSession],
    queryFn: async (): Promise<SessionDetail> => {
      if (!selectedSession) throw new Error('No session selected')
      const [messagesRes, conditionsRes, guidanceRes] = await Promise.all([
        api.get(`/api/engage/v1/session/${selectedSession}/messages`),
        api.get(`/api/engage/v1/session/${selectedSession}/conditions`),
        api.get(`/api/engage/v1/session/${selectedSession}/guidance`),
      ])
      return {
        id: selectedSession,
        messages: messagesRes.data,
        conditions: conditionsRes.data.conditions,
        guidance: guidanceRes.data,
      }
    },
    enabled: !!selectedSession,
    refetchInterval: selectedSession ? 3000 : false,
  })

  // Trajectory forecast for selected session
  const { data: forecast } = useTrajectoryForecast(selectedSession || '')

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [sessionDetail?.messages])

  const getChannelIcon = (channel: string) => {
    switch (channel) {
      case 'voice': return '📞'
      case 'chat': return '💬'
      case 'email': return '📧'
      case 'sms': return '📱'
      default: return '💬'
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'bg-green-500'
      case 'ended': return 'bg-gray-400'
      case 'error': return 'bg-red-500'
      default: return 'bg-gray-400'
    }
  }

  const formatTime = (timestamp: string) => {
    return new Date(timestamp).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  const formatRelativeTime = (timestamp: string) => {
    const diff = Date.now() - new Date(timestamp).getTime()
    const minutes = Math.floor(diff / 60000)
    if (minutes < 1) return 'Just now'
    if (minutes < 60) return `${minutes}m ago`
    const hours = Math.floor(minutes / 60)
    if (hours < 24) return `${hours}h ago`
    return `${Math.floor(hours / 24)}d ago`
  }

  return (
    <div className="h-[calc(100vh-12rem)] flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Real-Time Monitor</h1>
          <p className="text-gray-600 mt-1">
            Live session tracking with condition detection
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 px-3 py-1.5 bg-green-50 rounded-full">
            <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
            <span className="text-sm text-green-700 font-medium">
              {sessions?.filter(s => s.status === 'active').length || 0} Live
            </span>
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as 'all' | 'active' | 'ended')}
            className="px-4 py-2 border border-gray-200 rounded-lg text-sm"
          >
            <option value="active">Active only</option>
            <option value="ended">Ended</option>
            <option value="all">All sessions</option>
          </select>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 grid lg:grid-cols-3 gap-6 min-h-0">
        {/* Sessions List */}
        <div className="lg:col-span-1 card overflow-hidden flex flex-col">
          <div className="p-4 border-b border-gray-100">
            <h3 className="font-semibold text-gray-900">Sessions</h3>
          </div>
          <div className="flex-1 overflow-y-auto">
            {sessionsLoading ? (
              <div className="p-4 text-center text-gray-500">Loading...</div>
            ) : sessions?.length === 0 ? (
              <div className="p-8 text-center">
                <div className="text-4xl mb-3">💤</div>
                <p className="text-gray-500">No sessions found</p>
              </div>
            ) : (
              <AnimatePresence>
                {sessions?.map((session) => (
                  <motion.button
                    key={session.id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    onClick={() => setSelectedSession(session.id)}
                    className={`w-full p-4 border-b border-gray-50 text-left hover:bg-gray-50 transition-colors ${
                      selectedSession === session.id ? 'bg-engage-primary/5 border-l-4 border-l-engage-primary' : ''
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="text-lg">{getChannelIcon(session.channel)}</span>
                        <span className={`w-2 h-2 rounded-full ${getStatusColor(session.status)}`} />
                      </div>
                      <span className="text-xs text-gray-400">
                        {formatRelativeTime(session.last_activity_at)}
                      </span>
                    </div>
                    <div className="mt-2">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {session.agent_id || 'Unassigned'}
                      </p>
                      <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
                        <span>{session.message_count} msgs</span>
                        {session.condition_count > 0 && (
                          <span className="text-amber-600">{session.condition_count} conditions</span>
                        )}
                      </div>
                    </div>
                  </motion.button>
                ))}
              </AnimatePresence>
            )}
          </div>
        </div>

        {/* Session Detail */}
        <div className="lg:col-span-2 card overflow-hidden flex flex-col">
          {!selectedSession ? (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <div className="text-6xl mb-4 opacity-50">👆</div>
                <p className="text-gray-500">Select a session to view details</p>
              </div>
            </div>
          ) : (
            <>
              {/* Session Header */}
              <div className="p-4 border-b border-gray-100 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div>
                    <h3 className="font-semibold text-gray-900">Session Details</h3>
                    <p className="text-xs text-gray-500 font-mono">{selectedSession}</p>
                  </div>
                  <StreamModeIndicator mode={streamMode} compact />
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setShowDualStream(!showDualStream)}
                    className={`px-3 py-1.5 text-xs rounded-lg font-medium transition-colors ${
                      showDualStream
                        ? 'bg-engage-primary text-white'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    {showDualStream ? 'Hide' : 'Show'} Dual-Stream
                  </button>
                  <button
                    onClick={() => setSelectedSession(null)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    ✕
                  </button>
                </div>
              </div>

              {/* Conditions Bar with Feedback Buttons */}
              {sessionDetail?.conditions && sessionDetail.conditions.length > 0 && (
                <div className="px-4 py-3 bg-amber-50 border-b border-amber-100">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-medium text-amber-800">Detected:</span>
                    {sessionDetail.conditions.slice(-3).map((condition) => (
                      <div key={condition.id} className="flex items-center gap-1">
                        <ConditionBadge
                          type={condition.condition_type}
                          confidence={condition.confidence}
                          intensity={condition.intensity}
                        />
                        <button
                          onClick={() => setFeedbackModal({
                            open: true,
                            signalType: 'certainty',
                            interpretation: `${condition.condition_type} (${Math.round(condition.confidence * 100)}% confidence)`,
                          })}
                          className="text-xs text-amber-600 hover:text-amber-800 px-1"
                          title="Submit correction"
                        >
                          ✏️
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Dual-Stream Panel (toggleable) */}
              {showDualStream && selectedSession && (
                <div className="px-4 py-3 border-b border-gray-100 bg-gray-50">
                  <DualStreamPanel
                    sessionId={selectedSession}
                    invokerId={sessions?.find(s => s.id === selectedSession)?.agent_id || ''}
                  />
                </div>
              )}

              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-4 space-y-4">
                <AnimatePresence>
                  {sessionDetail?.messages.map((message, index) => (
                    <motion.div
                      key={message.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.05 }}
                      className={`flex ${message.speaker === 'agent' ? 'justify-end' : 'justify-start'}`}
                    >
                      <div
                        className={`max-w-[80%] rounded-2xl px-4 py-2 ${
                          message.speaker === 'agent'
                            ? 'bg-engage-primary text-white rounded-br-md'
                            : message.speaker === 'customer'
                            ? 'bg-gray-100 text-gray-900 rounded-bl-md'
                            : 'bg-gray-50 text-gray-600 text-sm italic'
                        }`}
                      >
                        <p className="text-sm">{message.content}</p>
                        <p className={`text-xs mt-1 ${
                          message.speaker === 'agent' ? 'text-white/70' : 'text-gray-400'
                        }`}>
                          {formatTime(message.timestamp)}
                        </p>
                      </div>
                    </motion.div>
                  ))}
                </AnimatePresence>
                <div ref={messagesEndRef} />
              </div>

              {/* Guidance Panel */}
              {sessionDetail?.guidance && sessionDetail.guidance.length > 0 && (
                <div className="p-4 border-t border-gray-100 bg-blue-50">
                  <h4 className="text-sm font-semibold text-blue-800 mb-2">
                    Latest Guidance
                  </h4>
                  <div className="text-sm text-blue-700">
                    {(sessionDetail.guidance[sessionDetail.guidance.length - 1]?.content as { suggestion?: string })?.suggestion ||
                     (sessionDetail.guidance[sessionDetail.guidance.length - 1]?.content as { headline?: string })?.headline ||
                     'Guidance available'}
                  </div>
                </div>
              )}

              {/* Trajectory Forecast */}
              {forecast && (
                <div className="px-4 py-3 border-t border-gray-100">
                  <TrajectoryForecastMini forecast={forecast} />
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Feedback Modal */}
      {feedbackModal.open && selectedSession && (
        <SignalFeedbackModal
          profileId={sessions?.find(s => s.id === selectedSession)?.profile_id || selectedSession}
          signalType={feedbackModal.signalType}
          originalInterpretation={feedbackModal.interpretation}
          onClose={() => setFeedbackModal({ open: false })}
        />
      )}
    </div>
  )
}
